<template>
  <button
    class="audit-btn"
    :class="{
      'audit-btn--active': isActive,
      'audit-btn--has-data': entryCount > 0
    }"
    @click="$emit('click')"
    :title="title">

    <!-- Status indicator light -->
    <span class="audit-btn__status">
      <span class="audit-btn__status-dot"></span>
    </span>

    <!-- Icon -->
    <span class="audit-btn__icon">
      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"/>
        <path
          d="M10 2C5.58172 2 2 5.58172 2 10C2 12.4 3.1 14.5 4.8 16"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-dasharray="2 3"/>
        <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
        <path
          d="M10 10V6"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"/>
        <path
          d="M10 10L13 12"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"/>
        <path
          d="M4 4L2 2M2 2V5M2 2H5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"/>
      </svg>
    </span>

    <!-- Label -->
    <span class="audit-btn__label">{{ label }}</span>

    <!-- Data counter -->
    <span v-if="entryCount > 0" class="audit-btn__counter">
      {{ formattedCount }}
    </span>

    <!-- Active scan line effect -->
    <span class="audit-btn__scanline"></span>
  </button>
</template>

<script>
export default {
  name: 'AuditHistoryButton',
  props: {
    label: {
      type: String,
      default: 'Audit Log'
    },
    title: {
      type: String,
      default: 'View audit history'
    },
    isActive: {
      type: Boolean,
      default: false
    },
    hasRecentActivity: {
      type: Boolean,
      default: false
    },
    entryCount: {
      type: Number,
      default: 0
    }
  },
  computed: {
    formattedCount() {
      if (this.entryCount >= 1000) {
        return (this.entryCount / 1000).toFixed(1) + 'k';
      }
      return this.entryCount.toString();
    }
  }
};
</script>

<style scoped>
/* ==============================================
   AUDIT HISTORY BUTTON
   Engineering Instrument Panel Aesthetic
   Clean • Precise • Professional
   ============================================== */

.audit-btn {
  --btn-height: 36px;
  --btn-radius: 6px;
  --btn-font: 'Roboto', -apple-system, sans-serif;

  /* Colors from app theme */
  --btn-bg: var(--v-bg2-base, #fcfcfc);
  --btn-bg-hover: var(--v-bg1-base, #f3f3f3);
  --btn-bg-active: var(--v-light_Primary-base, #EDF2E8);
  --btn-border: var(--v-secondary-base, #b0bec5);
  --btn-text: var(--v-grey-base, #484848);
  --btn-text-muted: var(--v-normal-grey-base, #727272);
  --btn-accent: var(--v-primary-base, #03624c);
  --btn-accent-dim: rgba(3, 98, 76, 0.15);

  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--btn-height);
  padding: 0 14px 0 10px;

  background: var(--btn-bg);
  border: 1px solid var(--btn-border);
  border-radius: var(--btn-radius);

  font-family: var(--btn-font);
  font-size: 13px;
  font-weight: 500;
  color: var(--btn-text);

  cursor: pointer;
  overflow: hidden;
  transition: all 0.15s ease;
  outline: none;
}

/* Hover */
.audit-btn:hover {
  background: var(--btn-bg-hover);
  border-color: var(--btn-accent);
}

/* Active (sidebar open) */
.audit-btn--active {
  background: var(--btn-bg-active);
  border-color: var(--btn-accent);
  color: var(--btn-accent);
}

/* Focus */
.audit-btn:focus-visible {
  box-shadow: 0 0 0 2px var(--btn-accent-dim);
}

/* ===================
   STATUS INDICATOR
   =================== */

.audit-btn__status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
}

.audit-btn__status-dot {
  width: 6px;
  height: 6px;
  background: var(--btn-border);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.audit-btn--has-data .audit-btn__status-dot {
  background: var(--btn-accent);
  box-shadow: 0 0 6px var(--btn-accent);
}

.audit-btn--active .audit-btn__status-dot {
  animation: statusPulse 1.5s ease-in-out infinite;
}

@keyframes statusPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.85);
  }
}

/* ===================
   ICON
   =================== */

.audit-btn__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: var(--btn-text-muted);
  transition: color 0.15s ease;
}

.audit-btn__icon svg {
  width: 100%;
  height: 100%;
}

.audit-btn:hover .audit-btn__icon,
.audit-btn--active .audit-btn__icon {
  color: var(--btn-accent);
}

/* ===================
   LABEL
   =================== */

.audit-btn__label {
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* ===================
   COUNTER
   =================== */

.audit-btn__counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  margin-left: 2px;

  background: var(--btn-accent-dim);
  border-radius: 4px;

  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--btn-accent);

  transition: all 0.15s ease;
}

.audit-btn:hover .audit-btn__counter,
.audit-btn--active .audit-btn__counter {
  background: var(--btn-accent);
  color: #fff;
}

/* ===================
   SCANLINE EFFECT
   =================== */

.audit-btn__scanline {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(3, 98, 76, 0.08) 50%,
    transparent 100%
  );
  pointer-events: none;
  opacity: 0;
}

.audit-btn--active .audit-btn__scanline {
  opacity: 1;
  animation: scanMove 2s linear infinite;
}

@keyframes scanMove {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* ===================
   DARK THEME
   =================== */

.theme--dark .audit-btn {
  --btn-bg: var(--v-bg3-base, #303030);
  --btn-bg-hover: var(--v-bg7-base, #464646);
  --btn-bg-active: rgba(3, 98, 76, 0.15);
  --btn-border: rgba(255, 255, 255, 0.12);
  --btn-text: var(--v-text-base, #ffffff);
  --btn-text-muted: var(--v-text_secondary-base, #C8C8C8);
}

.theme--dark .audit-btn:hover {
  border-color: var(--btn-accent);
}

/* ===================
   DISABLED
   =================== */

.audit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.audit-btn:disabled:hover {
  background: var(--btn-bg);
  border-color: var(--btn-border);
}
</style>
